import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../services/api';

const ConditionBoardList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [boats, setBoats] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<{ withBoard: number; total: number } | null>(null);

  useEffect(() => {
    loadBoats();
    loadStats();
  }, [page, rowsPerPage]);

  const loadBoats = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search) params.search = search;

      const data = await api.getConditionBoards(params);
      setBoats(data.data);
      setTotal(data.total);
    } catch (e: any) {
      setError(e.message || '로딩 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await api.getConditionBoardStats();
      setStats(data);
    } catch {}
  };

  const handleSearch = () => {
    setPage(0);
    loadBoats();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        조황 게시판 보유 배
      </Typography>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="primary">{stats.withBoard}</Typography>
                <Typography variant="body2" color="text.secondary">조황 게시판 보유</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="text.secondary">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">전체 배 (URL 보유)</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            size="small"
            label="배 이름 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
          />
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handleSearch}><SearchIcon /></IconButton>
            <IconButton onClick={() => { setSearch(''); setPage(0); loadBoats(); }}><RefreshIcon /></IconButton>
          </Stack>
        </Stack>
      </Paper>

      {isMobile ? (
        <Stack spacing={1.5}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : boats.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">데이터가 없습니다</Typography>
            </Paper>
          ) : (
            boats.map((boat) => (
              <Card key={boat.id}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        #{boat.id} · {boat.region || '지역없음'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {boat.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={boat.conditionBoardName || '게시판 없음'}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          스캔: {formatDate(boat.conditionScannedAt)}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack direction="column" sx={{ flexShrink: 0 }}>
                      {boat.websiteUrl && (
                        <IconButton size="small" component="a" href={boat.websiteUrl} target="_blank">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                      {boat.conditionBoardUrl && (
                        <IconButton size="small" component="a" href={boat.conditionBoardUrl} target="_blank">
                          <OpenInNewIcon fontSize="small" color="primary" />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage=""
          />
        </Stack>
      ) : (
      <TableContainer component={Paper}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>배 이름</TableCell>
                <TableCell>지역</TableCell>
                <TableCell>게시판 이름</TableCell>
                <TableCell>홈페이지</TableCell>
                <TableCell>게시판 URL</TableCell>
                <TableCell>스캔일</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {boats.map((boat) => (
                <TableRow key={boat.id} hover>
                  <TableCell>{boat.id}</TableCell>
                  <TableCell>{boat.name}</TableCell>
                  <TableCell>{boat.region || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={boat.conditionBoardName || '-'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {boat.websiteUrl && (
                      <IconButton size="small" component="a" href={boat.websiteUrl} target="_blank">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>
                    {boat.conditionBoardUrl && (
                      <IconButton size="small" component="a" href={boat.conditionBoardUrl} target="_blank">
                        <OpenInNewIcon fontSize="small" color="primary" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(boat.conditionScannedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>
      )}
    </Box>
  );
};

export default ConditionBoardList;
